/** @file Top header bar with user menu + logout. */
import { authStore } from "../core/auth-store.js";
import { navigate } from "../core/router.js";
import { logout } from "../persistence/auth.js";
import { h, clear } from "./dom.js";
import { toggleHallenMode, isHallenMode } from "./hallen-mode.js";

/** @type {HTMLElement | null} */
let root = null;
/** @type {(() => void) | null} */
let unsub = null;

/**
 * @param {HTMLElement} parent
 */
export function mountHeader(parent) {
  if (root) {
    return;
  }
  root = h("header", { class: "hb-header" });
  parent.appendChild(root);
  render();
  unsub = authStore.subscribe(render);
}

export function unmountHeader() {
  unsub?.();
  unsub = null;
  root?.remove();
  root = null;
}

function render() {
  if (!root) {
    return;
  }
  const { status, user } = authStore.get();
  clear(root);

  const logo = h("a", { class: "hb-header__logo", href: "#/" }, ["Handballplan"]);
  root.appendChild(logo);
  const right = h("div", { class: "hb-header__right" });
  root.appendChild(right);

  // Hallen-Modus toggle (available for both signed-in and signed-out users).
  const inHalle = isHallenMode();
  const hallenBtn = h(
    "button",
    {
      type: "button",
      class: "hb-btn min-h-11 min-w-11",
      "aria-pressed": inHalle ? "true" : "false",
      "aria-label": inHalle ? "Hallen-Modus aus" : "Hallen-Modus an",
      "data-testid": "hallen-toggle",
      title: inHalle ? "Hallen-Modus aus" : "Hallen-Modus an",
    },
    [inHalle ? "🏟 an" : "🏟 aus"],
  );
  hallenBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleHallenMode().catch((err) => console.warn("[hallen]", err));
  });
  right.appendChild(hallenBtn);

  if (status === "initializing") {
    right.appendChild(h("span", { class: "hb-spinner", "aria-label": "Lade…" }));
    return;
  }

  if (status === "signedOut") {
    const btn = h("button", { type: "button", class: "hb-btn hb-btn--primary min-h-11" }, [
      "Anmelden",
    ]);
    btn.addEventListener("click", () => navigate("login"));
    right.appendChild(btn);
    return;
  }

  // signedIn
  const initial = (user?.displayName?.[0] ?? user?.email?.[0] ?? "?").toUpperCase();
  const menuBtn = h(
    "button",
    {
      type: "button",
      class: "hb-header__avatar",
      "aria-label": "Benutzer-Menü",
      "aria-expanded": "false",
    },
    [initial],
  );
  right.appendChild(menuBtn);

  const menu = h("div", { class: "hb-header__menu", role: "menu", "data-open": "false" });
  right.appendChild(menu);

  /** @type {{ label: string, action: () => void }[]} */
  const items = [
    { label: "Meine Spielzüge", action: () => navigate("plays") },
    { label: "Teams", action: () => navigate("teams") },
    { label: "Konto", action: () => navigate("account") },
    { label: "Anleitung", action: () => navigate("help") },
    {
      label: "Abmelden",
      action: () => {
        logout().catch((e) => console.warn("[auth] logout failed", e));
      },
    },
  ];
  for (const it of items) {
    const btn = h(
      "button",
      { type: "button", class: "hb-header__menu-item min-h-11", role: "menuitem" },
      [it.label],
    );
    btn.addEventListener("click", () => {
      menu.setAttribute("data-open", "false");
      menuBtn.setAttribute("aria-expanded", "false");
      it.action();
    });
    menu.appendChild(btn);
  }

  menuBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const open = menu.getAttribute("data-open") === "true";
    menu.setAttribute("data-open", open ? "false" : "true");
    menuBtn.setAttribute("aria-expanded", open ? "false" : "true");
  });
  document.addEventListener(
    "click",
    () => {
      menu.setAttribute("data-open", "false");
      menuBtn.setAttribute("aria-expanded", "false");
    },
    { once: true },
  );
}

/**
 * @param {boolean} visible
 */
export function setHeaderVisible(visible) {
  if (root) {
    root.style.display = visible ? "" : "none";
  }
}
